import { Component, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { ButtonDirective } from 'primeng/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { CheckboxComponent } from '@components/checkbox';
import { Field, FieldBaseGetQuery, FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { FieldExportType } from '@models/export-file-type';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  AgroclimaticZone,
  AgroclimaticZoneService,
  CropService,
  SoilType,
  SoilTypeService,
  FirmService,
  Person,
  Firm,
  FieldType,
  FieldTypeService,
  RegionService,
} from '@6grain/masofa-monolith-api-client/dictionaries';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { IPolygonColor } from '@components/request-map/request-map.component2';
import { FilterOperator } from '@6grain/masofa-monolith-api-client/crop-monitoring/model/filterOperator';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { ModalEditFieldPolygon } from '@app/pages/electronic-field-map/pages/fields/passport/forms/modal-edit-field-polygon/modal-edit-field-polygon';
import { Loading } from '@components/ui/loading/loading';
import { MsfNumber } from '@app/utils/pipes/format-number';
import { FieldCropsColorPipe } from '@app/utils/pipes/field-crops-color-pipe';
import { CommonModule } from '@angular/common';
import { PassportMenuGeneralHistoryComponent } from '@app/pages/electronic-field-map/pages/fields/passport/menu/passport-menu-general-history/passport-menu-general-history.component';
import { PassportGeneralMap } from '@app/pages/electronic-field-map/pages/fields/passport/menu/passport-menu-general/passport-general-map/passport-general-map';
import { PassportManagerService } from '@app/pages/electronic-field-map/pages/fields/passport/passport-manager.service';
import { RegionDisplay } from '@components/region-display/region-display';
import {
  ModalFieldEditExtended
} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/modal-field-edit-extended/modal-field-edit-extended';
import { Menu } from "primeng/menu";
import {MenuItem} from 'primeng/api';

@Component({
  selector: 'app-fields',
  templateUrl: './passport-menu-general.html',
  imports: [
    ButtonDirective,
    TranslatePipe,
    FormsModule,
    CheckboxComponent,
    ServerTranslatePipe,
    Loading,
    MsfNumber,
    CommonModule,
    PassportMenuGeneralHistoryComponent,
    PassportGeneralMap,
    RegionDisplay,
    Menu
],
  providers: [DialogService, FieldCropsColorPipe],
  styleUrl: './passport-menu-general.scss',
})
export class PassportMenuGeneral implements OnInit, OnDestroy {
  protected destroy$: Subject<void> = new Subject<void>();
  @ViewChild(PassportMenuGeneralHistoryComponent)
  private historyComponent?: PassportMenuGeneralHistoryComponent;

  private readonly regex = /^(.*?)(?=\/)/;
  ref: DynamicDialogRef | undefined;

  protected readonly translate: TranslateService = inject(TranslateService);
  serviceMethod = 'cropMonitoringFieldGetByIdIdGet';
  fieldId?: string | null | undefined;
  fields: Field[] = [];
  soilTypes: SoilType[] = [];

  $destroy$ = new Subject<void>();

  persons: Person[] = [];
  firms: Firm[] = [];

  soilType!: SoilType | undefined;
  agroclimaticZone!: AgroclimaticZone | undefined;
  fieldType!: FieldType | undefined;

  isLoading: boolean = false;
  isLoadingMetaData: boolean = false;
  cadastre?: string | null | undefined;

  multiPolygonJson: IPolygonColor[] = [];

  public isExporting = false;
  readonly exportMenuItems = signal<MenuItem[]>([]);

  constructor(
    public dialogService: DialogService,
    public cropService: CropService,
    public service: FieldService,
    protected activatedRoute: ActivatedRoute,
    public regionService: RegionService,
    public soilTypeService: SoilTypeService,
    public agroClimaticService: AgroclimaticZoneService,
    public firmService: FirmService,
    public fieldTypeService: FieldTypeService,
    public passportManager: PassportManagerService,
  ) {
    this.initExportMenu();
  }

  ngOnInit(): void {
    this.fieldId = this.activatedRoute.parent?.snapshot.paramMap.get('id');

    this.passportManager.afterLoadField
      .pipe(takeUntil(this.$destroy$))
      .subscribe((field: Field) => {
        this.isLoading = false;
        this.loadMetaData(field);
      });

    this.loadField(this.fieldId);
  }

  openEditModal(): void {
    this.ref = this.dialogService.open(ModalFieldEditExtended, {
      header: this.translate.instant('General information'),
      width: '60%',
      height:'96%',
      closable: true,
      modal: true,

      data: {
        model: { ...this.passportManager.field()?.entity },
        serviceMethod: 'cropMonitoringFieldUpdatePut',
        service: this.service,
        fields: this.passportManager.getEditFieldFormConfig(),
      },
    });

    this.ref.onClose.subscribe((result) => {
      if (result?.success) {
        this.passportManager
          .loadField(this.fieldId as string)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.historyComponent?.reloadHistory();
          });
      }
    });
  }

  loadField(fieldId?: string | null | undefined, forceReload: boolean = false): void {
    if (!fieldId) return;

    this.isLoading = true;

    // Если поле уже загружено, просто загружаем метаданные
    if (!forceReload && this.passportManager.field()) {
      this.loadMetaData(this.passportManager.field()?.entity as Field);
      return;
    }

    // Загружаем поле и подписываемся на результат
    this.passportManager
      .loadField(fieldId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (field: any) => {
          this.isLoading = false;
          if (field?.entity) {
            this.loadMetaData(field?.entity);
          }
        },
        error: (error) => {
          console.error('Error loading field:', error);
          this.isLoading = false;
        }
      });
  }

  loadMetaData(field: Field) {
    if (!field) return;

    this.isLoadingMetaData = true;

    forkJoin({
      agroclimaticZones: this.agroClimaticService
        .dictionariesAgroclimaticZoneGetByQueryPost({
          filters: [
            {
              filterField: 'id',
              filterOperator: FilterOperator.NUMBER_0,
              filterValue: field.agroclimaticZoneId,
            },
          ],
        })
        .pipe(catchError(() => of([]))),

      soilTypes: this.soilTypeService
        .dictionariesSoilTypeGetByQueryPost({
          offset: 0,
          filters: [
            {
              filterField: 'id',
              filterOperator: FilterOperator.NUMBER_0,
              filterValue: field.soilTypeId,
            },
          ],
        })
        .pipe(catchError(() => of([]))),

      fieldTypes: this.fieldTypeService
        .dictionariesFieldTypeGetByQueryPost({
          filters: (field as any).fieldTypeId
            ? [
                {
                  filterField: 'id',
                  filterOperator: FilterOperator.NUMBER_0,
                  filterValue: (field as any).fieldTypeId,
                },
              ]
            : [],
        })
        .pipe(catchError(() => of([]))),
    }).subscribe(({ soilTypes, agroclimaticZones, fieldTypes }) => {
      this.soilTypes = soilTypes ?? [];
      this.agroclimaticZone = agroclimaticZones.find((a) => a.id === field?.agroclimaticZoneId);
      this.soilType = this.soilTypes.find((s) => s.id === field?.soilTypeId);
      this.fieldType = fieldTypes?.find((ft) => ft.id === (field as any)?.fieldTypeId);
      this.isLoading = false;
      this.isLoadingMetaData = false;
    });
  }

  private initExportMenu(): void {
      this.exportMenuItems.set([
        {
          label: this.translate.instant('GeoJson'),
          command: () => this.exportFields(FieldExportType.GeoJson)
        },
        {
          label: this.translate.instant('KML'),
          command: () => this.exportFields(FieldExportType.KML)
        },
        {
          label: this.translate.instant('ShapeFile'),
          command: () => this.exportFields(FieldExportType.ShapeFile)
        },
        {
          label: this.translate.instant('Csv'),
          command: () => this.exportFields(FieldExportType.Csv)
        }
      ]);
  }

  exportFields(format: FieldExportType = FieldExportType.KML): void {
    const fieldId = this.fieldId;
    if (!fieldId) return;

    this.isExporting = true;

    const filters = [
      {
        filterField: 'id',
        filterOperator: FilterOperator.NUMBER_0,
        filterValue: fieldId
      }
    ];

    const exportCall$ = format === FieldExportType.Csv
      ? this.exportAsCSV(filters)
      : this.exportByFormat(filters, format);

    exportCall$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.handleExportResponse(response, format);
          this.isExporting = false;
        },
        error: (err) => {
          console.error('Export failed:', err);
          this.isExporting = false;
        }
      });
  }

  private exportAsCSV(filters: any) {
    const params: FieldBaseGetQuery = {
      filters: filters || []
    };

    return this.service.cropMonitoringFieldExportFromCSVPost(
      params,
      'body',
      false,
      { httpHeaderAccept: 'application/octet-stream' } as any
    );
  }

  private exportByFormat(filters: any, format: FieldExportType) {
    const params = this.buildExportParams(filters, format);

    return this.service.cropMonitoringFieldExportFieldsPost(
      params as any,
      'body',
      false,
      { httpHeaderAccept: 'application/octet-stream' } as any
    );
  }

  private buildExportParams(filters: Object[], format: FieldExportType): unknown {
    return {
      fieldsQuery: { filters },
      fieldExportType: format
    };
  }

  private handleExportResponse(response: unknown, format: FieldExportType): void {
    if (response instanceof Blob) {
      this.downloadFile(response, format);
      return;
    }

    const asAny = response as { body?: Blob };
    if (asAny?.body instanceof Blob) {
      this.downloadFile(asAny.body, format);
    }
  }

  private downloadFile(blob: Blob, format: FieldExportType): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `field-export${this.getExtensionForFormat(format)}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }

  private getExtensionForFormat(format: FieldExportType): string {
    const extensions: Record<FieldExportType, string> = {
      [FieldExportType.GeoJson]: '.zip',
      [FieldExportType.KML]: '.zip',
      [FieldExportType.Csv]: '.csv',
      [FieldExportType.ShapeFile]: '.zip'
    };
    return extensions[format] || '';
  }

  onEditDetails() {
    this.ref = this.dialogService.open(ModalEditFieldPolygon, {
      header: this.translate.instant('General information (maps)'),
      width: '55%',
      closable: true,
      modal: true,
      data: {
        model: this.passportManager.field()?.entity,
        serviceMethod: 'cropMonitoringFieldUpdatePut',
        service: this.service,
        multiPolygonJson: this.multiPolygonJson,
      },
    });

    this.ref.onClose.subscribe((result) => {
      if (result) {
        this.loadField(this.fieldId, true);
      }
    });
  }

  onMarkerClick($event: { lat: number; lng: number }) {}

  get fieldStatus(): boolean {
    return this.passportManager.field()?.entity?.status === 1;
  }

  set fieldStatus(value: boolean) {
    if (this.passportManager.field()?.entity) {
      const field = this.passportManager.field()?.entity as Field;
      field.status = value ? 1 : 0;
      this.passportManager.setField({ entity: field });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
